import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import { PARTNERS_CONTACT_EMAIL } from "./constants";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Heart } from "lucide-react";

const ORG_TYPES = [
  { v: "dv_org",        label: "Domestic violence organization" },
  { v: "advocacy",      label: "Advocacy group" },
  { v: "shelter",       label: "Shelter or transitional housing" },
  { v: "therapist",     label: "Therapist or clinical practice" },
  { v: "legal_aid",     label: "Legal aid or pro-bono attorney" },
  { v: "community",     label: "Community partner / nonprofit" },
  { v: "other",         label: "Something else" },
];

export default function Partners() {
  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    email: "",
    role: "",
    organization_type: "dv_org",
    website: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization || !form.contact_name || !form.email || !form.message) {
      toast.info("Please fill out the required fields.");
      return;
    }
    setBusy(true);
    try {
      // No auth header needed — public form. The /api/partners/inquiry
      // endpoint is rate-limited per source IP.
      await api.post("/partners/inquiry", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingShell>
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid="page-partners">
        <p className="font-script text-2xl text-brand">collaborate</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">Partners & Organizations.</h1>
        <p className="mt-5 text-lg text-foreground/75 leading-relaxed">
          Sister Signal is at its best when it works alongside the advocates, shelters, therapists,
          legal aid offices, and community organizations who already serve survivors. We'd love to find
          ways to support each other.
        </p>

        <section className="mt-10 rounded-3xl bg-brand-soft/40 border border-brand/20 p-7">
          <Heart className="w-5 h-5 text-brand" />
          <p className="mt-2 font-serif text-2xl leading-snug">Ways we collaborate.</p>
          <ul className="mt-3 space-y-2 text-foreground/80 leading-relaxed">
            <li>· Co-branded survivor education resources</li>
            <li>· In-app referrals to your local services</li>
            <li>· Training and demos for advocates</li>
            <li>· Joint grant applications</li>
            <li>· Quiet referral pathways with consent built in</li>
          </ul>
        </section>

        {done ? (
          <section className="mt-10 rounded-3xl bg-card border border-brand/30 p-8 text-center" data-testid="partners-success">
            <CheckCircle2 className="w-10 h-10 text-brand mx-auto" />
            <p className="mt-4 font-serif text-3xl">Thank you.</p>
            <p className="mt-3 text-foreground/75 leading-relaxed max-w-md mx-auto">
              We've received your message. A member of our team will reach out, usually within a week.
              For urgent matters, please email <a className="text-brand underline" href={`mailto:${PARTNERS_CONTACT_EMAIL}`}>{PARTNERS_CONTACT_EMAIL}</a>.
            </p>
            <Link to="/" className="mt-6 inline-flex items-center gap-1 text-brand hover:underline">
              Back to home <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="partners-form">
            <Field label="Organization *" required>
              <input data-testid="partners-organization"
                value={form.organization} onChange={update("organization")}
                className="w-full h-12 rounded-2xl bg-card border border-border px-4" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name *" required>
                <input data-testid="partners-name"
                  value={form.contact_name} onChange={update("contact_name")}
                  className="w-full h-12 rounded-2xl bg-card border border-border px-4" />
              </Field>
              <Field label="Email *" required>
                <input data-testid="partners-email" type="email"
                  value={form.email} onChange={update("email")}
                  className="w-full h-12 rounded-2xl bg-card border border-border px-4" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Role">
                <input data-testid="partners-role"
                  value={form.role} onChange={update("role")} placeholder="e.g. Director, Advocate"
                  className="w-full h-12 rounded-2xl bg-card border border-border px-4" />
              </Field>
              <Field label="Organization type">
                <select data-testid="partners-orgtype"
                  value={form.organization_type} onChange={update("organization_type")}
                  className="w-full h-12 rounded-2xl bg-card border border-border px-4">
                  {ORG_TYPES.map((o) => (
                    <option key={o.v} value={o.v}>{o.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Website">
              <input data-testid="partners-website"
                value={form.website} onChange={update("website")} placeholder="https://"
                className="w-full h-12 rounded-2xl bg-card border border-border px-4" />
            </Field>
            <Field label="How would you like to collaborate? *" required>
              <textarea data-testid="partners-message"
                value={form.message} onChange={update("message")}
                rows={6}
                className="w-full rounded-2xl bg-card border border-border px-4 py-3 leading-relaxed" />
            </Field>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button type="submit" disabled={busy} data-testid="partners-submit"
                className="px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all disabled:opacity-60">
                {busy ? "Sending…" : "Send inquiry"}
              </button>
              <a href={`mailto:${PARTNERS_CONTACT_EMAIL}`}
                 className="text-sm text-foreground/70 hover:text-brand">
                or email {PARTNERS_CONTACT_EMAIL}
              </a>
            </div>
          </form>
        )}
      </article>
    </MarketingShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-foreground/55 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
